// src/components/common/Button.jsx
import React from "react";

const variants = {
  green: `
    bg-gradient-to-r from-[#00A260] to-[#00C878]
    text-white
    shadow-[0_8px_24px_rgba(0,162,96,0.22)]
    hover:from-[#00C878] hover:to-[#00A260]
    hover:shadow-[0_12px_30px_rgba(0,162,96,0.30)]
  `,

  orange: `
    bg-gradient-to-r from-[#F97316] to-[#FB923C]
    text-white
    shadow-[0_8px_24px_rgba(249,115,22,0.20)]
    hover:from-[#FB923C] hover:to-[#F97316]
    hover:shadow-[0_12px_30px_rgba(249,115,22,0.28)]
  `,

  navy: `
    bg-gradient-to-r from-[#061A3A] to-[#0B2A4A]
    text-white
    shadow-[0_8px_24px_rgba(6,26,58,0.18)]
    hover:from-[#0B2A4A] hover:to-[#061A3A]
    hover:shadow-[0_12px_30px_rgba(6,26,58,0.26)]
  `,

  outlineGreen: `
    border-2 border-[#00A260]
    bg-white
    text-[#008C54]
    hover:bg-[#00A260]
    hover:text-white
    hover:border-[#00A260]
    hover:shadow-[0_8px_24px_rgba(0,162,96,0.16)]
  `,

  outlineNavy: `
    border-2 border-[#061A3A]
    bg-white
    text-[#061A3A]
    hover:bg-[#061A3A]
    hover:text-white
    hover:border-[#061A3A]
    hover:shadow-[0_8px_24px_rgba(6,26,58,0.16)]
  `,

  ghost: `
    bg-transparent
    text-[#061A3A]
    hover:bg-[#F1F5F3]
    hover:text-[#005543]
  `,
};

const sizes = {
  xs: "min-h-[36px] px-3 text-xs rounded-lg",
  sm: "min-h-[40px] px-4 text-sm rounded-xl",
  md: "min-h-[46px] px-5 text-sm rounded-xl",
  lg: "min-h-[52px] px-6 text-base rounded-2xl",
  xl: "min-h-[58px] px-7 text-base sm:text-lg rounded-2xl",
};

const Button = ({
  children,
  variant = "green",
  size = "md",
  className = "",
  fullWidth = false,
  as = "button",
  icon: Icon,
  iconPosition = "left",
  loading = false,
  disabled = false,
  type,
  ...rest
}) => {
  const Comp = as;

  const isDisabled = disabled || loading;

  const classes = [
    // Base
    "group relative inline-flex items-center justify-center",
    "gap-2 overflow-hidden",
    "font-semibold tracking-[-0.01em]",
    "select-none whitespace-nowrap",

    // Smooth animation
    "transition-all duration-300 ease-out",
    "motion-safe:hover:-translate-y-0.5",
    "motion-safe:active:translate-y-0",
    "motion-safe:active:scale-[0.97]",

    // Touch
    "touch-manipulation",

    // Focus
    "focus:outline-none",
    "focus-visible:ring-4",
    "focus-visible:ring-[#00A260]/20",
    "focus-visible:ring-offset-2",

    // Disabled
    "disabled:pointer-events-none",
    "disabled:opacity-50",
    "disabled:shadow-none",

    // Variant
    variants[variant] || variants.green,

    // Size
    sizes[size] || sizes.md,

    // Width
    fullWidth ? "w-full" : "",

    className,
  ].join(" ");

  const iconClasses = [
    "shrink-0",
    "transition-transform duration-300 ease-out",
    "motion-safe:group-hover:scale-110",
    iconPosition === "right"
      ? "motion-safe:group-hover:translate-x-1"
      : "motion-safe:group-hover:-translate-x-0.5",
  ].join(" ");

  return (
    <Comp
      className={classes}
      disabled={isDisabled}
      type={as === "button" ? type || "button" : undefined}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      {...rest}
    >
      {/* Subtle shine animation */}
      {variant !== "ghost" && (
        <span
          aria-hidden="true"
          className="
            pointer-events-none absolute inset-0
            -translate-x-full
            bg-gradient-to-r
            from-transparent
            via-white/15
            to-transparent
            transition-transform duration-700
            motion-safe:group-hover:translate-x-full
          "
        />
      )}

      {/* Content */}
      <span className="relative z-10 inline-flex items-center justify-center gap-2">
        {loading ? (
          <span
            className="
              h-4 w-4 animate-spin rounded-full
              border-2 border-current border-t-transparent
            "
            aria-hidden="true"
          />
        ) : (
          Icon && iconPosition !== "right" && (
            <Icon className={`${iconClasses} h-4 w-4`} aria-hidden="true" />
          )
        )}

        {loading ? "Please wait..." : children}

        {!loading &&
          Icon &&
          iconPosition === "right" && (
            <Icon
              className={`${iconClasses} h-4 w-4`}
              aria-hidden="true"
            />
          )}
      </span>
    </Comp>
  );
};

export default Button;